import React, { useCallback, useEffect, useState } from 'react';
import {
  Alert, Box, Button, CircularProgress, InputAdornment, Paper, Table, TableBody,
  TableCell, TableContainer, TableHead, TableRow, TextField, Typography
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import { useNavigate } from 'react-router-dom';
import { getRentalCustomers } from '../../services/api';
import { getErrorMessage } from '../../utils/errorMessage';
import ExportButtons from '../../components/ExportButtons';
import { dash, fmtExportDateTime, fmtExportMoney } from '../../utils/tableExport';

const money = (v) =>
  parseFloat(v || 0).toLocaleString('en-LK', { style: 'currency', currency: 'LKR' });

const RentalCustomers = () => {
  const navigate = useNavigate();
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [search, setSearch] = useState('');

  const load = useCallback(async (q = '') => {
    try {
      setLoading(true);
      setError('');
      const res = await getRentalCustomers(q ? { search: q } : {});
      setRows(res.data || []);
    } catch (err) {
      setError(getErrorMessage(err, 'Failed to load rental customers'));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
    const onBranch = () => load(search);
    window.addEventListener('branch-filter-changed', onBranch);
    return () => window.removeEventListener('branch-filter-changed', onBranch);
  }, [load]);

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', flexWrap: 'wrap', gap: 2, mb: 2 }}>
        <Box>
          <Typography variant="h5" fontWeight={700}>Rental customer profiles</Typography>
          <Typography variant="body2" color="text.secondary">
            Customers with rentals — current contracts, history, and balances.
          </Typography>
        </Box>
        <ExportButtons
          title="Rental Customers"
          filename="rental_customers"
          columns={[
            { label: 'Customer', value: (r) => dash(r.customer_name) },
            { label: 'Phone', value: (r) => dash(r.customer_phone) },
            { label: 'Rentals', value: (r) => r.rental_count },
            { label: 'Current', value: (r) => r.current_count },
            { label: 'Billed', value: (r) => fmtExportMoney(r.total_billed) },
            { label: 'Balance', value: (r) => fmtExportMoney(r.balance_due) },
            { label: 'Last rental', value: (r) => fmtExportDateTime(r.last_rental_at) }
          ]}
          rows={rows}
        />
      </Box>

      {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>{error}</Alert>}

      <Box component="form" onSubmit={(e) => { e.preventDefault(); load(search.trim()); }} sx={{ mb: 2 }}>
        <TextField
          size="small"
          fullWidth
          placeholder="Search name, phone, or company"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start"><SearchIcon fontSize="small" /></InputAdornment>
            )
          }}
        />
      </Box>

      {loading ? (
        <CircularProgress />
      ) : (
        <TableContainer component={Paper}>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>Customer</TableCell>
                <TableCell>Phone</TableCell>
                <TableCell align="right">Rentals</TableCell>
                <TableCell align="right">Current</TableCell>
                <TableCell align="right">Billed</TableCell>
                <TableCell align="right">Balance</TableCell>
                <TableCell>Last rental</TableCell>
                <TableCell />
              </TableRow>
            </TableHead>
            <TableBody>
              {rows.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={8}>
                    <Typography color="text.secondary">No rental customers found.</Typography>
                  </TableCell>
                </TableRow>
              ) : rows.map((r) => (
                <TableRow key={r.customer_id} hover>
                  <TableCell>
                    {r.customer_name}
                    {r.company_name ? (
                      <Typography variant="caption" display="block" color="text.secondary">{r.company_name}</Typography>
                    ) : null}
                  </TableCell>
                  <TableCell>{r.customer_phone || '—'}</TableCell>
                  <TableCell align="right">{r.rental_count}</TableCell>
                  <TableCell align="right">{r.current_count}</TableCell>
                  <TableCell align="right">{money(r.total_billed)}</TableCell>
                  <TableCell align="right">{money(r.balance_due)}</TableCell>
                  <TableCell>{r.last_rental_number || '—'}</TableCell>
                  <TableCell>
                    <Button size="small" onClick={() => navigate(`/rentals/customers/${r.customer_id}`)}>
                      Profile
                    </Button>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}
    </Box>
  );
};

export default RentalCustomers;
